import { createAsyncThunk } from "@reduxjs/toolkit";
import { showSnackbar } from "../../snackbar/Snackbar.slice";
import { post, get, put } from "../../../services/apiService";
import { getInvoicesList } from "../../lists/Invoices/Invoices.thunk";
import { toExpiresAtFormDataValue } from "./InvoiceForm.resources";

const INVOICE_CREATE = "invoiceForm/create";
const INVOICE_UPDATE = "invoiceForm/update";
const INVOICE_GET_BY_ID = "invoiceForm/getById";
const INVOICE_GET_CURRENCIES = "invoiceForm/getCurrencies";
const INVOICE_SEND_EMAIL = "invoiceForm/sendEmail";

export const getInvoiceById = createAsyncThunk(
  INVOICE_GET_BY_ID,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { token, invoiceId } = payload;
      const url = `/invoices/${invoiceId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const invoiceData = await get(url, config);
      return {
        invoiceData,
        invoiceId: invoiceData.id,
      };
    } catch (error) {
      console.error("Error getting invoice by id thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error retrieving invoice",
          severity: "error",
        }),
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error retrieving invoice",
      });
    }
  },
);

export const createInvoice = createAsyncThunk(
  INVOICE_CREATE,
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { token, handleGoBack, invoice_file, expires_at, ...invoiceData } =
        payload;
      const url = "/invoices";

      const formData = new FormData();
      Object.entries(invoiceData).forEach(([key, value]) => {
        if (key === "expires_at" || key === "no_expiration") {
          return;
        }
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      formData.append("expires_at", toExpiresAtFormDataValue(expires_at));

      if (invoice_file && invoice_file instanceof File) {
        formData.append("invoice_file", invoice_file);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const result = await post(url, formData, config);

      // Send email after successful creation
      if (result?.id) {
        try {
          await dispatch(
            sendInvoiceEmail({ token, invoiceId: result.id }),
          ).unwrap();
          dispatch(
            showSnackbar({
              message: "Invoice email sent successfully",
              severity: "success",
            }),
          );
        } catch (emailError) {
          // Log error but don't fail the creation
          dispatch(
            showSnackbar({
              message:
                "Error sending invoice email: " + emailError ||
                "Error sending invoice email",
              severity: "error",
            }),
          );
        }
      }

      dispatch(
        showSnackbar({
          message: "Invoice created successfully",
          severity: "success",
        }),
      );

      // Reload the list before navigating back to avoid duplicate calls
      const state = getState();
      const { client_info_id, page, pageSize } = state.invoices;
      if (client_info_id) {
        dispatch(
          getInvoicesList({
            token,
            page,
            page_size: pageSize,
            client_info_id,
          }),
        );
      }

      if (handleGoBack) {
        handleGoBack();
      }
      return result;
    } catch (error) {
      console.error("Error creating invoice thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error creating invoice",
          severity: "error",
        }),
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error creating invoice",
      });
    }
  },
);

export const updateInvoice = createAsyncThunk(
  INVOICE_UPDATE,
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const {
        token,
        handleGoBack,
        invoiceId,
        invoice_file,
        expires_at,
        ...invoiceData
      } = payload;
      const url = `/invoices/${invoiceId}`;

      const formData = new FormData();
      Object.entries(invoiceData).forEach(([key, value]) => {
        if (key === "expires_at" || key === "no_expiration") {
          return;
        }
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      formData.append("expires_at", toExpiresAtFormDataValue(expires_at));

      if (invoice_file && invoice_file instanceof File) {
        formData.append("invoice_file", invoice_file);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const result = await put(url, formData, config);

      dispatch(
        showSnackbar({
          message: "Invoice updated successfully",
          severity: "success",
        }),
      );

      // Send email after successful creation

      try {
        await dispatch(sendInvoiceEmail({ token, invoiceId })).unwrap();
        dispatch(
          showSnackbar({
            message: "Invoice email sent successfully",
            severity: "success",
          }),
        );
      } catch (emailError) {
        // Log error but don't fail the creation
        dispatch(
          showSnackbar({
            message:
              "Error sending invoice email: " + emailError ||
              "Error sending invoice email",
            severity: "error",
          }),
        );
      }

      if (handleGoBack) {
        handleGoBack();
      }
      return { invoiceData: result, invoiceId };
    } catch (error) {
      console.error("Error updating invoice thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error updating invoice",
          severity: "error",
        }),
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error updating invoice",
      });
    }
  },
);

export const getCurrencies = createAsyncThunk(
  INVOICE_GET_CURRENCIES,
  async (payload, { rejectWithValue }) => {
    const { token } = payload;
    const url = `/currencies`;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await get(url, config);
      return {
        currencies: data.map(
          ({ currency_alpha3_code }) => currency_alpha3_code,
        ),
      };
    } catch (error) {
      console.error("Error getting currencies thunk:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const sendInvoiceEmail = createAsyncThunk(
  INVOICE_SEND_EMAIL,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { token, invoiceId } = payload;
      const url = `/invoices/${invoiceId}/send-email`;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      const result = await post(url, {}, config);
      dispatch(
        showSnackbar({
          message: "Invoice email sent successfully",
          severity: "success",
        }),
      );
      return result;
    } catch (error) {
      console.error("Error sending invoice email thunk:", error);
      dispatch(
        showSnackbar({
          message:
            error.response?.data?.detail ||
            error.message ||
            "Error sending invoice email",
          severity: "warning",
        }),
      );
      return rejectWithValue({
        message:
          error.response?.data?.detail ||
          error.message ||
          "Error sending invoice email",
      });
    }
  },
);
